"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PendingTradeNotification } from "@/components/PendingTradeNotification";
import { fetchWithCsrf } from "@/lib/api/fetch-csrf";

type AuthState = { isRegistered: boolean } | null;

export function Nav() {
  const [auth, setAuth] = useState<AuthState>(null);
  const pathname = usePathname();

  // Re-check auth whenever the route changes. Client-side navigation (e.g.
  // router.push("/dashboard") after login) doesn't remount this component,
  // so without this the nav can show stale state — user logs in, sees
  // "Login" button, clicks it, gets bounced back.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/auth/me", { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setAuth(d); })
      .catch(() => { if (!cancelled) setAuth(null); });
    return () => { cancelled = true; };
  }, [pathname]);

  async function handleLogout() {
    await fetchWithCsrf("/api/v1/auth/logout", { method: "POST" });
    setAuth({ isRegistered: false });
    window.location.href = "/";
  }

  return (
    <header className="border-b border-border bg-background">
      {/* Layout: logo flush left; nav cluster right with a clear two-group
          hierarchy — content links (Sell, Dashboard) | account actions
          (theme, login/logout). The vertical divider gives the eye a
          visual breakpoint so a quick glance can find the action you
          want. py-4 (was py-3) gives the 40px logo room to breathe. */}
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4">
        <Link href="/" aria-label="Smokoin home" className="inline-flex items-center shrink-0">
          <Logo className="h-9 sm:h-10 w-auto" />
        </Link>

        <nav className="flex items-center gap-3 sm:gap-5">
          {/* Content navigation. Sell Crypto stays visible on every
              breakpoint — it's the primary call-to-action and the
              fastest path to revenue, so we keep it in the nav even on
              narrow screens. Dashboard hides below sm because logged-in
              users land on /dashboard anyway after sign-in. */}
          <div className="flex items-center gap-3 sm:gap-5">
            <Link href="/sell" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              Sell Crypto
            </Link>
            {auth?.isRegistered && (
              <Link href="/dashboard" className="hidden sm:inline-flex text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Dashboard
              </Link>
            )}
          </div>

          {/* Vertical separator. Renders on every breakpoint now since
              there's always something on each side (Sell on the left,
              actions on the right). */}
          <div className="h-5 w-px bg-border shrink-0" aria-hidden />

          {/* Account actions cluster — pending-trade bell + theme toggle
              + auth button(s). The bell renders nothing for visitors
              with no pending trades, so first-time / logged-out visitors
              don't see a dormant icon. */}
          <div className="flex items-center gap-2 sm:gap-3">
            <PendingTradeNotification />
            <ThemeToggle />
            {auth?.isRegistered ? (
              <Button variant="outline" size="sm" onClick={handleLogout}>
                Logout
              </Button>
            ) : (
              <>
                <Link href="/auth/login" className="hidden sm:inline-flex">
                  <Button variant="ghost" size="sm">Login</Button>
                </Link>
                <Link href="/auth/register">
                  <Button size="sm">Register</Button>
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}
